/**
 * Evict any service worker found on the manager's origin — §9.282, FB-440.
 *
 * **The manager is not a PWA and has never registered one**, so anything here arrived from somewhere
 * else: the customer build, served on this origin during a repoint or a misconfigured vhost. The
 * owner's report is the symptom — *"when I open hkm.toxik.space, sometimes I see zákaznícku
 * stránku, and also sometimes the theme is reverted from Dark to White"* — and both halves have the
 * one cause, because the customer bundle carries `BrandingApplier`.
 *
 * **A 404 does not undo a registration.** `hkm.toxik.space/service-worker.js` already answers 404 and
 * the stale worker keeps serving from its own cache regardless: once installed on an origin it lives
 * until it is unregistered, the storage is cleared, or the server sends `Clear-Site-Data`. Nothing
 * AGOS deploys can reach it from the outside, so the page has to evict it from the inside.
 *
 * `getRegistrations()` is scoped to this origin, so this can never touch the customer app's own
 * worker on the customer's own domain.
 */

/** Marks the reload as done for this tab, so a worker that keeps reappearing cannot loop. */
const RELOADED = 'agos.sw-evicted'

export async function evictServiceWorkers(): Promise<boolean> {
  // Absent on insecure origins and in some embedded browsers. A manager that failed to start because
  // it could not tidy up would be a far worse bug than the one this fixes.
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return false

  let evicted = false
  try {
    const registrations = await navigator.serviceWorker.getRegistrations()
    for (const registration of registrations) {
      // `unregister()` answers false when it had already gone, which is not an eviction.
      if (await registration.unregister()) evicted = true
    }
  } catch {
    // A browser that refuses to enumerate its registrations is one this cannot help. Silence is
    // right: there is nothing the person at the till could do with the error.
  }

  if (!evicted) return false

  try {
    // **The caches go with it.** Unregistering stops the worker intercepting future requests and
    // leaves behind the Cache Storage it filled, which a later worker — or any `caches.match` — could
    // still serve. Both, or neither.
    if (typeof caches !== 'undefined') {
      const keys = await caches.keys()
      await Promise.all(keys.map((key) => caches.delete(key)))
    }
  } catch {
    // Same reasoning: the worker is already gone, which is the half that matters.
  }

  return true
}

/**
 * The whole job, including the one reload that makes it visible.
 *
 * A worker that has already claimed the page is serving **this document**, so the tab in front of
 * somebody stays wrong until it is reloaded — without this the fix appears not to work on the very
 * visit that performs it. Once per tab, because a reload loop is worse than a stale worker.
 */
export async function evictAndReload(): Promise<void> {
  const evicted = await evictServiceWorkers()
  if (!evicted) return
  try {
    if (sessionStorage.getItem(RELOADED) === '1') return
    sessionStorage.setItem(RELOADED, '1')
  } catch {
    // Private modes can refuse `sessionStorage`. Without the mark a reload could repeat, so the
    // safer failure is to skip it: the worker is unregistered either way and the next navigation is
    // clean.
    return
  }
  location.reload()
}
